import { History, TriangleAlert } from 'lucide-react';
import type { Unit } from '@eombor/contracts';
import { EmptyState, Skeleton } from '@eombor/ui';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';
import { useUsages } from '../model/storekeeping.queries.js';
import { initialsOf } from './UsageHistoryView.js';

/**
 * «BU MAHSULOTNI KIM OLGAN?» — bitta LOT bo'yicha ro'yxat.
 *
 * IKKI joyda kerak va ikkalasida ham bir xil bo'lishi SHART:
 *
 *   - qoldiq kartochkasida («3 dona kelgan edi, javonda nega bitta?»);
 *   - RASXOD oynasida — akt aynan shu olishlar uchun yoziladi va xodim
 *     «nimani chiqaryapman» degan savolga javobni ko'z oldida ko'rishi,
 *     sababni esa shu yerdan ko'chirib yozishi kerak.
 *
 * Ro'yxat QISQA bo'ladi (bitta lotdan o'nlab marta olinmaydi), shuning
 * uchun sahifalash yo'q: birinchi ellik yozuv olinadi.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

const momentText = (iso: string): string => {
  const d = new Date(iso);
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const qtyText = (qty: number): string => (Math.round(qty * 1000) / 1000).toLocaleString('ru-RU');

/**
 * Kartochka ko'rsatadigan NARSA — manbaning o'zi emas.
 *
 * Ikki joydan keladi va ikkalasi ham shu shaklga to'g'ri keladi:
 * bo'linmaning ishlatish tarixi (`StockUsageRow`) va rasxod aktining
 * dalili (`WriteOffUsageDto` + nomenklatura birligi). Kartochkani
 * ularning kattarog'iga bog'lab qo'yish ikkinchi joyda ishlatishni
 * imkonsiz qilardi.
 */
export interface LotUsageItem {
  id: string;
  workerName: string;
  qty: number;
  unit: string;
  usedAt: string;
  comment: string;
}

/** Bitta olish — kim, nechta, qachon va O'Z so'zlari bilan nima uchun. */
export const LotUsageCard = ({ item }: { item: LotUsageItem }) => (
  <li className="flex flex-wrap items-start gap-3 rounded-[var(--radius-md)] border border-border bg-surface p-3">
    <span
      aria-hidden
      className="flex size-9 shrink-0 items-center justify-center rounded-full bg-accent-soft text-xs font-semibold text-accent"
    >
      {initialsOf(item.workerName)}
    </span>
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <p className="flex flex-wrap items-baseline gap-x-2 text-sm text-text">
        <span className="font-semibold">{item.workerName}</span>
        <span className="tabular font-semibold text-warn">
          −{qtyText(item.qty)} {unitText(item.unit as Unit)}
        </span>
        <span className="tabular ml-auto text-xs text-text-muted">{momentText(item.usedAt)}</span>
      </p>
      {/* Izoh — xodimning O'Z so'zlari: «nima uchun olindi» degan
          savolga javob faqat shu yerda bor. */}
      <blockquote className="border-l-2 border-accent/40 pl-2 text-sm text-pretty text-text-muted italic">
        {item.comment}
      </blockquote>
    </div>
  </li>
);

export const LotUsageList = ({
  lotId,
  unit,
  /** Ro'yxat bo'sh bo'lsa umuman chizilmaydi — rasxod oynasi shunday ishlatadi. */
  hideWhenEmpty = false,
}: {
  lotId: string;
  unit: string;
  hideWhenEmpty?: boolean;
}) => {
  const usages = useUsages({ lotId, limit: 50 });
  const items = usages.data?.items ?? [];
  const taken = items.reduce((sum, item) => sum + item.qty, 0);

  if (usages.isPending) return hideWhenEmpty ? null : <Skeleton className="h-32 w-full" />;

  if (usages.isError) {
    /* Rasxod oynasida tarix YORDAMCHI ma'lumot: uni o'qiy olmaslik
       (masalan ruxsat yo'q) aktni yozishga to'sqinlik qilmasligi kerak. */
    return hideWhenEmpty ? null : (
      <EmptyState
        icon={<TriangleAlert size={20} aria-hidden />}
        title={t('myStock.loadError')}
        description={usages.error.message}
      />
    );
  }

  if (items.length === 0) {
    return hideWhenEmpty ? null : (
      <EmptyState
        icon={<History size={20} aria-hidden />}
        title={t('lotUsers.empty')}
        description={t('lotUsers.emptyHint')}
      />
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Jami BIRINCHI: kartochkadagi «javonda 0» raqamining izohi shu. */}
      <p className="text-sm text-text-muted">
        {t('lotUsers.total', {
          qty: qtyText(taken),
          unit: unitText(unit as Unit),
          count: items.length,
        })}
      </p>

      <ul className="flex flex-col gap-2">
        {items.map((item) => (
          <LotUsageCard key={item.id} item={item} />
        ))}
      </ul>
    </div>
  );
};
